import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Badge,
  Alert,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  History as HistoryIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Router as DeviceIcon,
  Done as DoneIcon,
  FilterList as FilterIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { monitoringApi } from '../../services/monitoringApi';
import { useSnackbar } from 'notistack';
import { useMonitoringWebSocket } from '../../hooks/useMonitoringWebSocket';

// Helper function to format time ago
const formatTimeAgo = (dateString) => {
  if (!dateString) return 'Never';

  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;

  if (diffMs < 0) return 'Just now';

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  const parts = [];

  if (weeks > 0) {
    parts.push(`${weeks}w`);
    const remainingDays = days % 7;
    if (remainingDays > 0) parts.push(`${remainingDays}d`);
  } else if (days > 0) {
    parts.push(`${days}d`);
    const remainingHours = hours % 24;
    if (remainingHours > 0) parts.push(`${remainingHours}h`);
  } else if (hours > 0) {
    parts.push(`${hours}h`);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`);
  } else if (minutes > 0) {
    parts.push(`${minutes}m`);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds > 0) parts.push(`${remainingSeconds}s`);
  } else {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ') + ' ago';
};

// Helper function to format date/time in user's local timezone
const formatLocalTime = (dateString, options = {}) => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';

  const defaultOptions = {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...options
  };

  return date.toLocaleTimeString(undefined, defaultOptions);
};

const formatLocalDateTime = (dateString, options = {}) => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';

  const defaultOptions = {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...options
  };

  return date.toLocaleString(undefined, defaultOptions);
};

// Fleet Overview Component
const FleetOverview = ({ stats, loading }) => {
  const { t } = useThemeSettings();

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('monitoring.fleetOverview') || 'Fleet Overview'}
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                bgcolor: 'success.light',
                borderRadius: 1,
                color: 'success.contrastText'
              }}
            >
              <Typography variant="h3">{stats.online || 0}</Typography>
              <Typography>{t('monitoring.online') || 'Online'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                bgcolor: 'error.light',
                borderRadius: 1,
                color: 'error.contrastText'
              }}
            >
              <Typography variant="h3">{stats.offline || 0}</Typography>
              <Typography>{t('monitoring.offline') || 'Offline'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                bgcolor: 'warning.light',
                borderRadius: 1,
                color: 'warning.contrastText'
              }}
            >
              <Typography variant="h3">{stats.warning || 0}</Typography>
              <Typography>{t('monitoring.warning') || 'Warning'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                bgcolor: 'grey.300',
                borderRadius: 1
              }}
            >
              <Typography variant="h3">{stats.unknown || 0}</Typography>
              <Typography>{t('monitoring.unknown') || 'Unknown'}</Typography>
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

// Alerts Panel Component - Enhanced with manual resolution
const AlertsPanel = ({ alerts, alertsSummary, alertHistory, onAcknowledge, onMarkAsFixed, onCleanupDuplicates, onDeviceClick, loading }) => {
  const { t } = useThemeSettings();
  const [showHistory, setShowHistory] = useState(false);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'info';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return 'Unknown';

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 && days === 0) parts.push(`${minutes}m`);
    if (secs > 0 && days === 0 && hours === 0) parts.push(`${secs}s`);

    return parts.length > 0 ? parts.join(' ') : '0s';
  };

  const extractSecondsFromMessage = (message) => {
    if (!message) return null;
    const match = message.match(/(\d+)\s*seconds?/i);
    return match ? parseInt(match[1], 10) : null;
  };

  const formatLastSeen = (alert) => {
    const seconds = extractSecondsFromMessage(alert.message);
    if (!seconds) return null;
    const triggeredAt = new Date(alert.triggeredAt);
    const lastSeen = new Date(triggeredAt.getTime() - (seconds * 1000));
    return formatLocalDateTime(lastSeen.toISOString());
  };

  const hasDuplicates = alertsSummary?.totalActive > alertsSummary?.uniqueDevicesAffected;

  return (
    <Paper sx={{ p: 2, maxHeight: 600, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            {t('monitoring.activeAlerts') || 'Active Alerts'}
          </Typography>
          <Badge
            badgeContent={alertsSummary?.uniqueDevicesAffected || alerts.length}
            color="error"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {hasDuplicates && onCleanupDuplicates && (
            <Button
              size="small"
              color="warning"
              onClick={onCleanupDuplicates}
            >
              Fix Duplicates
            </Button>
          )}
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setShowHistory(!showHistory)}
            variant={showHistory ? 'contained' : 'outlined'}
          >
            {showHistory ? 'Hide History' : 'History'}
          </Button>
        </Box>
      </Box>

      {alertsSummary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={`${alertsSummary.uniqueDevicesAffected} devices affected`}
            color="error"
            variant="outlined"
          />
          {alertsSummary.resolvedInPeriod > 0 && (
            <Chip
              size="small"
              label={`${alertsSummary.resolvedInPeriod} resolved (${alertsSummary.period})`}
              color="success"
              variant="outlined"
            />
          )}
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : alerts.length === 0 ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography>{t('monitoring.noAlerts') || 'No active alerts - all systems operational'}</Typography>
        </Alert>
      ) : (
        <>
          {alerts.map((alert) => {
            const seconds = extractSecondsFromMessage(alert.message);
            const lastSeen = formatLastSeen(alert);
            const deviceId = alert.deviceDbId || alert.device_db_id || alert.device?.id;

            return (
              <Alert
                key={alert.id}
                severity={getSeverityColor(alert.severity)}
                sx={{
                  mb: 1,
                  cursor: deviceId ? 'pointer' : 'default',
                  transition: 'background-color 0.2s',
                  '&:hover': deviceId ? { bgcolor: 'action.hover' } : {}
                }}
                onClick={() => {
                  if (deviceId && onDeviceClick) {
                    onDeviceClick(deviceId);
                  }
                }}
                action={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Mark as Fixed">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkAsFixed(alert.id);
                        }}
                      >
                        <DoneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Acknowledge">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAcknowledge(alert.id);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle2">{alert.title}</Typography>
                  {deviceId && (
                    <Tooltip title="View device">
                      <OpenInNewIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 1 }} />
                    </Tooltip>
                  )}
                </Box>
                {seconds ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                      Offline for {formatDuration(seconds)}
                    </Typography>
                    {lastSeen && (
                      <Typography variant="body2" color="text.secondary">
                        Last seen: {lastSeen}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2">{alert.message}</Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {alert.location?.friendlyName && (
                    <Chip size="small" label={alert.location.friendlyName} sx={{ height: 20, fontSize: '0.75rem' }} />
                  )}
                  {alert.device?.friendlyName && (
                    <Chip size="small" label={alert.device.friendlyName} variant="outlined" sx={{ height: 20, fontSize: '0.75rem' }} />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {formatLocalDateTime(alert.triggeredAt)}
                  </Typography>
                </Box>
              </Alert>
            );
          })}
        </>
      )}

      {showHistory && alertHistory && alertHistory.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Recently Resolved
          </Typography>
          {alertHistory.map((alert) => (
            <Box
              key={alert.id}
              sx={{
                p: 1,
                mb: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
                borderLeft: '3px solid',
                borderColor: 'success.main'
              }}
            >
              <Typography variant="body2" sx={{ textDecoration: 'line-through', opacity: 0.7 }}>
                {alert.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Triggered: {formatLocalDateTime(alert.triggeredAt)}
                </Typography>
                {alert.resolvedAt && (
                  <Typography variant="caption" color="success.main">
                    Resolved: {formatLocalDateTime(alert.resolvedAt)}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </>
      )}
    </Paper>
  );
};

// Problem Devices Table Component - Replaces DeviceGrid
const ProblemDevicesTable = ({ devices, onDeviceClick, loading }) => {
  const { t } = useThemeSettings();
  const [orderBy, setOrderBy] = useState('lastHeartbeat');
  const [order, setOrder] = useState('asc');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'ok': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'dead': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ok': return <CheckCircleIcon fontSize="small" color="success" />;
      case 'warning': return <WarningIcon fontSize="small" color="warning" />;
      case 'error': return <ErrorIcon fontSize="small" color="error" />;
      case 'dead': return <ErrorIcon fontSize="small" />;
      default: return <InfoIcon fontSize="small" />;
    }
  };

  // Filter to only show problem devices (unknown, error, dead)
  const problemDevices = useMemo(() => {
    return devices.filter(device => {
      const isProblem = ['unknown', 'error', 'dead', 'warning'].includes(device.status);
      if (!isProblem) return false;

      if (filter !== 'all' && device.status !== filter) return false;

      if (search) {
        const searchLower = search.toLowerCase();
        const name = (device.name || device.friendlyName || device.device_UUID || '').toLowerCase();
        const location = (device.location?.friendlyName || '').toLowerCase();
        return name.includes(searchLower) || location.includes(searchLower);
      }

      return true;
    });
  }, [devices, filter, search]);

  // Sort devices
  const sortedDevices = useMemo(() => {
    return [...problemDevices].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'name':
          aValue = a.name || a.friendlyName || a.device_UUID || '';
          bValue = b.name || b.friendlyName || b.device_UUID || '';
          break;
        case 'status':
          const statusOrder = { dead: 0, error: 1, unknown: 2, warning: 3, ok: 4 };
          aValue = statusOrder[a.status] ?? 5;
          bValue = statusOrder[b.status] ?? 5;
          break;
        case 'location':
          aValue = a.location?.friendlyName || '';
          bValue = b.location?.friendlyName || '';
          break;
        case 'lastHeartbeat':
        default:
          aValue = a.lastHeartbeat ? new Date(a.lastHeartbeat).getTime() : 0;
          bValue = b.lastHeartbeat ? new Date(b.lastHeartbeat).getTime() : 0;
          break;
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    });
  }, [problemDevices, orderBy, order]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            {t('monitoring.problemDevices') || 'Problem Devices'}
          </Typography>
          <Chip label={problemDevices.length} size="small" color="error" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ width: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filter}
              label="Status"
              onChange={(e) => setFilter(e.target.value)}
            >
              <MenuItem value="all">All Issues</MenuItem>
              <MenuItem value="dead">Dead</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : sortedDevices.length === 0 ? (
        <Alert severity="success">
          {filter === 'all'
            ? 'No problem devices - all systems operational!'
            : `No devices with status "${filter}"`}
        </Alert>
      ) : (
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Device
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'location'}
                    direction={orderBy === 'location' ? order : 'asc'}
                    onClick={() => handleSort('location')}
                  >
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'lastHeartbeat'}
                    direction={orderBy === 'lastHeartbeat' ? order : 'asc'}
                    onClick={() => handleSort('lastHeartbeat')}
                  >
                    Last Online
                  </TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedDevices.map((device) => (
                <TableRow
                  key={device.id || device.deviceId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onDeviceClick(device)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(device.status)}
                      <Chip
                        label={device.status?.toUpperCase() || 'UNKNOWN'}
                        color={getStatusColor(device.status)}
                        size="small"
                        sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {device.name || device.friendlyName || device.device_UUID}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {device.device_UUID}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {device.location?.friendlyName || '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TimeIcon fontSize="small" color="action" />
                      <Typography variant="body2" color={device.lastHeartbeat ? 'text.primary' : 'error'}>
                        {formatTimeAgo(device.lastHeartbeat)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDeviceClick(device); }}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

// Expandable Service Status Component
const ServiceStatusSummary = ({ services, onDeviceClick, loading }) => {
  const { t } = useThemeSettings();
  const [expandedService, setExpandedService] = useState(null);
  const [affectedDevices, setAffectedDevices] = useState({});
  const [loadingDevices, setLoadingDevices] = useState({});

  const handleExpand = async (serviceName) => {
    if (expandedService === serviceName) {
      setExpandedService(null);
      return;
    }

    setExpandedService(serviceName);

    if (!affectedDevices[serviceName]) {
      setLoadingDevices(prev => ({ ...prev, [serviceName]: true }));
      try {
        const result = await monitoringApi.getAffectedDevices(serviceName);
        if (result.success) {
          setAffectedDevices(prev => ({ ...prev, [serviceName]: result.data.devices || [] }));
        }
      } catch (error) {
        console.error('Failed to load affected devices:', error);
      } finally {
        setLoadingDevices(prev => ({ ...prev, [serviceName]: false }));
      }
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('monitoring.services') || 'Service Status'}
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : Object.keys(services).length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          {t('monitoring.noServices') || 'No services available'}
        </Typography>
      ) : (
        Object.entries(services).map(([name, status]) => {
          const isExpanded = expandedService === name;
          const hasIssues = status.ok < status.total;
          const devices = affectedDevices[name] || [];

          return (
            <Box key={name} sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: hasIssues ? 'warning.light' : 'success.light',
                  cursor: hasIssues ? 'pointer' : 'default',
                  '&:hover': hasIssues ? { opacity: 0.9 } : {}
                }}
                onClick={() => hasIssues && handleExpand(name)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {hasIssues ? (
                    <WarningIcon fontSize="small" color="warning" />
                  ) : (
                    <CheckCircleIcon fontSize="small" color="success" />
                  )}
                  <Typography>{name.replace(/_/g, ' ')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`${status.ok || 0}/${status.total || 0}`}
                    color={hasIssues ? 'warning' : 'success'}
                    size="small"
                  />
                  {hasIssues && (
                    isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                  )}
                </Box>
              </Box>

              <Collapse in={isExpanded && hasIssues}>
                <Box sx={{ pl: 2, pt: 1 }}>
                  {loadingDevices[name] ? (
                    <CircularProgress size={20} />
                  ) : devices.length > 0 ? (
                    <List dense>
                      {devices.map((device, idx) => (
                        <ListItem
                          key={device.id || idx}
                          button
                          onClick={() => onDeviceClick(device)}
                          sx={{ borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <DeviceIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={device.name || device.friendlyName || device.device_UUID}
                            secondary={device.location?.friendlyName}
                          />
                          <Chip
                            size="small"
                            label={device.status}
                            color={device.status === 'ok' ? 'success' : 'warning'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Unable to load affected devices
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          );
        })
      )}
    </Paper>
  );
};

// Uptime History Component with detailed hover and click-through dialog
const UptimeHistory = ({ uptimeData, loading }) => {
  const { t } = useThemeSettings();
  const theme = useTheme();
  const navigate = useNavigate();
  const [detailDialog, setDetailDialog] = useState({ open: false, date: null, data: null });
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [allEvents, setAllEvents] = useState([]);
  const [pagination, setPagination] = useState({ offset: 0, limit: 100, total: 0, hasMore: false });
  const [eventSort, setEventSort] = useState({ field: 'timestamp', direction: 'desc' });

  const history = uptimeData?.dailyUptime || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'up':
      case 'operational':
        return theme.palette.success.main;
      case 'partial':
      case 'degraded':
        return theme.palette.warning.main;
      case 'down':
      case 'outage':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[400];
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'up':
      case 'operational':
        return 'Operational';
      case 'partial':
      case 'degraded':
        return 'Partial Outage';
      case 'down':
      case 'outage':
        return 'Major Outage';
      default:
        return 'No Data';
    }
  };

  const handleDayClick = async (day) => {
    setDetailDialog({ open: true, date: day.date, data: null });
    setLoadingDetail(true);
    setAllEvents([]);
    setPagination({ offset: 0, limit: 100, total: 0, hasMore: false });
    setLoadingAll(false);

    try {
      const result = await monitoringApi.getUptimeDetails(day.date, { offset: 0, limit: 100 });
      if (result.success) {
        setDetailDialog(prev => ({ ...prev, data: result.data }));
        setAllEvents(result.data.events || []);
        setPagination(result.data.pagination || { offset: 0, limit: 100, total: 0, hasMore: false });
      }
    } catch (error) {
      console.error('Failed to load uptime details:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleLoadMore = async () => {
    if (!detailDialog.date || loadingMore || !pagination.hasMore) return;

    setLoadingMore(true);
    const newOffset = pagination.offset + pagination.limit;

    try {
      const result = await monitoringApi.getUptimeDetails(detailDialog.date, {
        offset: newOffset,
        limit: pagination.limit
      });
      if (result.success && result.data.events) {
        setAllEvents(prev => [...prev, ...result.data.events]);
        setPagination(result.data.pagination || { ...pagination, hasMore: false });
      }
    } catch (error) {
      console.error('Failed to load more events:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleLoadAll = async () => {
    if (!detailDialog.date || loadingAll) return;

    setLoadingAll(true);
    const remaining = pagination.total - allEvents.length;

    try {
      const result = await monitoringApi.getUptimeDetails(detailDialog.date, {
        offset: allEvents.length,
        limit: remaining
      });
      if (result.success && result.data.events) {
        setAllEvents(prev => [...prev, ...result.data.events]);
        setPagination(prev => ({ ...prev, hasMore: false }));
      }
    } catch (error) {
      console.error('Failed to load all events:', error);
    } finally {
      setLoadingAll(false);
    }
  };

  const handleDeviceClick = (deviceId) => {
    if (deviceId) {
      setDetailDialog({ open: false, date: null, data: null });
      navigate(`/devices?selectedId=${deviceId}`);
    }
  };

  const handleEventSort = (field) => {
    setEventSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Sort events client-side
  const sortedEvents = useMemo(() => {
    if (!allEvents || allEvents.length === 0) return [];

    return [...allEvents].sort((a, b) => {
      let aVal, bVal;

      switch (eventSort.field) {
        case 'timestamp':
          aVal = new Date(a.timestamp).getTime();
          bVal = new Date(b.timestamp).getTime();
          break;
        case 'type':
          aVal = a.type || '';
          bVal = b.type || '';
          break;
        case 'checkType':
          aVal = a.checkType || '';
          bVal = b.checkType || '';
          break;
        case 'deviceName':
          aVal = (a.deviceName || a.deviceUuid || '').toLowerCase();
          bVal = (b.deviceName || b.deviceUuid || '').toLowerCase();
          break;
        case 'locationName':
          aVal = (a.locationName || '').toLowerCase();
          bVal = (b.locationName || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (eventSort.direction === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });
  }, [allEvents, eventSort]);

  const CustomTooltip = ({ day }) => (
    <Box sx={{ p: 1, minWidth: 200 }}>
      <Typography variant="subtitle2" fontWeight="bold">{day.date}</Typography>
      <Divider sx={{ my: 0.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body2">Status:</Typography>
        <Typography variant="body2" color={day.status === 'up' ? 'success.main' : 'warning.main'}>
          {getStatusLabel(day.status)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body2">Uptime:</Typography>
        <Typography variant="body2">{day.uptime || 100}%</Typography>
      </Box>
      {day.incidents > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Incidents:</Typography>
          <Typography variant="body2" color="error">{day.incidents}</Typography>
        </Box>
      )}
      {day.affectedDevices > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Affected Devices:</Typography>
          <Typography variant="body2">{day.affectedDevices}</Typography>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Click for details
      </Typography>
    </Box>
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          <Typography variant="h6">
            {t('monitoring.uptimeHistory') || 'Uptime History'}
          </Typography>
        </Box>
        <Chip
          label={`${uptimeData?.overallUptime || '99.9'}% uptime`}
          color="success"
          size="small"
          icon={<TrendingUpIcon />}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              height: 32,
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: 'action.hover',
              mb: 1
            }}
          >
            {history.length > 0 ? (
              history.map((day, index) => (
                <Tooltip
                  key={index}
                  title={<CustomTooltip day={day} />}
                  arrow
                  placement="top"
                >
                  <Box
                    onClick={() => handleDayClick(day)}
                    sx={{
                      flex: 1,
                      bgcolor: getStatusColor(day.status),
                      borderRight: index < history.length - 1 ? '1px solid rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.2s',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.7,
                        transform: 'scaleY(1.1)'
                      }
                    }}
                  />
                </Tooltip>
              ))
            ) : (
              Array.from({ length: 90 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: 1,
                    bgcolor: theme.palette.success.main,
                    borderRight: index < 89 ? '1px solid rgba(0,0,0,0.1)' : 'none'
                  }}
                />
              ))
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'text.secondary' }}>
            <Typography variant="caption">90 days ago</Typography>
            <Typography variant="caption">Today</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'success.main' }} />
              <Typography variant="caption">Operational</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'warning.main' }} />
              <Typography variant="caption">Partial outage</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'error.main' }} />
              <Typography variant="caption">Outage</Typography>
            </Box>
          </Box>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, date: null, data: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Uptime Details - {detailDialog.date}
        </DialogTitle>
        <DialogContent>
          {loadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : detailDialog.data ? (
            <Box>
              {/* Stats Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="h4" color={detailDialog.data.stats?.uptime >= 99 ? 'success.main' : detailDialog.data.stats?.uptime >= 95 ? 'warning.main' : 'error.main'}>
                        {detailDialog.data.stats?.uptime || 100}%
                      </Typography>
                      <Typography variant="body2">Uptime</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="h4" color="error.main">
                        {detailDialog.data.stats?.incidents || 0}
                      </Typography>
                      <Typography variant="body2">Incidents</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="h4">
                        {detailDialog.data.stats?.affectedDevices || 0}
                      </Typography>
                      <Typography variant="body2">Affected Devices</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="h4" color="info.main">
                        {detailDialog.data.affectedServices?.length || 0}
                      </Typography>
                      <Typography variant="body2">Affected Services</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Check Type Breakdown */}
              {detailDialog.data.checkTypeBreakdown?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Check Type Breakdown</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {detailDialog.data.checkTypeBreakdown.map((ct, idx) => (
                      <Chip
                        key={idx}
                        label={`${ct.checkType}: ${ct.successRate}% (${ct.successes}/${ct.total})`}
                        color={ct.successRate >= 99 ? 'success' : ct.successRate >= 90 ? 'warning' : 'error'}
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Affected Services */}
              {detailDialog.data.affectedServices?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Affected Services</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Service</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Devices Affected</TableCell>
                          <TableCell>Sample Devices</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailDialog.data.affectedServices.map((service, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {service.serviceName?.replace(/_/g, ' ')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={service.status}
                                color={service.status === 'error' || service.status === 'dead' ? 'error' : 'warning'}
                              />
                            </TableCell>
                            <TableCell>{service.deviceCount}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {service.devices?.slice(0, 3).map((device, dIdx) => (
                                  <Chip
                                    key={dIdx}
                                    size="small"
                                    label={device.name || device.uuid}
                                    onClick={() => handleDeviceClick(device.id)}
                                    sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                                  />
                                ))}
                                {service.devices?.length > 3 && (
                                  <Chip size="small" label={`+${service.devices.length - 3} more`} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Events Table with Pagination and Sorting */}
              {allEvents.length > 0 ? (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      Events ({allEvents.length} of {pagination.total})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Click column headers to sort
                    </Typography>
                  </Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={eventSort.field === 'timestamp'}
                              direction={eventSort.field === 'timestamp' ? eventSort.direction : 'desc'}
                              onClick={() => handleEventSort('timestamp')}
                            >
                              Time
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={eventSort.field === 'type'}
                              direction={eventSort.field === 'type' ? eventSort.direction : 'desc'}
                              onClick={() => handleEventSort('type')}
                            >
                              Type
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={eventSort.field === 'checkType'}
                              direction={eventSort.field === 'checkType' ? eventSort.direction : 'desc'}
                              onClick={() => handleEventSort('checkType')}
                            >
                              Check
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={eventSort.field === 'deviceName'}
                              direction={eventSort.field === 'deviceName' ? eventSort.direction : 'desc'}
                              onClick={() => handleEventSort('deviceName')}
                            >
                              Device
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={eventSort.field === 'locationName'}
                              direction={eventSort.field === 'locationName' ? eventSort.direction : 'desc'}
                              onClick={() => handleEventSort('locationName')}
                            >
                              Location
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedEvents.map((event, idx) => (
                          <TableRow
                            key={event.id || idx}
                            hover
                            sx={{ cursor: event.deviceId ? 'pointer' : 'default' }}
                            onClick={() => handleDeviceClick(event.deviceId)}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {formatLocalTime(event.timestamp)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={event.type}
                                color={event.type === 'failure' || event.type === 'error' ? 'error' : 'warning'}
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{event.checkType}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {event.deviceName || event.deviceUuid || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {event.locationName || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={event.errorMessage || '-'}>
                                <Typography variant="caption" color="error" sx={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {event.errorMessage || '-'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Load More / Load All Buttons */}
                  {pagination.hasMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                        disabled={loadingMore || loadingAll}
                        startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                      >
                        {loadingMore ? 'Loading...' : `Load More (+${pagination.limit})`}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleLoadAll}
                        disabled={loadingMore || loadingAll}
                        startIcon={loadingAll ? <CircularProgress size={16} color="inherit" /> : null}
                      >
                        {loadingAll ? 'Loading All...' : `Load All (${pagination.total - allEvents.length} remaining)`}
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Alert severity="success">No failure events on this day</Alert>
              )}
            </Box>
          ) : (
            <Alert severity="info">No data available for this date</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, date: null, data: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// Simplified System Health Card - Only shows issues
const SystemHealthCard = ({ systemHealth, loading }) => {
  const { t } = useThemeSettings();

  const services = systemHealth?.services || {};
  const unhealthyServices = Object.entries(services).filter(([, s]) => s.healthy === false);
  const allHealthy = unhealthyServices.length === 0;

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {t('monitoring.systemHealth') || 'System Health'}
        </Typography>
        <Button
          size="small"
          endIcon={<OpenInNewIcon />}
          href="https://monitoring.caro.care"
          target="_blank"
        >
          Status Page
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Alert
            severity={allHealthy ? 'success' : 'error'}
            icon={allHealthy ? <CheckCircleIcon /> : <ErrorIcon />}
          >
            {allHealthy
              ? (t('monitoring.allOperational') || 'All systems operational')
              : `${unhealthyServices.length} service(s) experiencing issues`}
          </Alert>

          {/* Only show unhealthy services */}
          {!allHealthy && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {unhealthyServices.map(([name, service]) => (
                <Box
                  key={name}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: 'error.light',
                    borderRadius: 1
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2">{service.displayName || name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {service.description || service.error || 'Service unavailable'}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label="Down"
                    color="error"
                    icon={<ErrorIcon />}
                  />
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

// System Activity Graph Component
const SystemActivityGraph = ({ loading }) => {
  const theme = useTheme();
  const [activityData, setActivityData] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);

  useEffect(() => {
    const loadActivityData = async () => {
      setLoadingActivity(true);
      try {
        const result = await monitoringApi.getActivityStatistics({ hours: 24 });
        if (result.success && result.data.hourlyStats) {
          // Convert server times to local timezone
          const localizedData = result.data.hourlyStats.map(stat => {
            // fullHour contains the full datetime string from server
            if (stat.fullHour) {
              const date = new Date(stat.fullHour);
              const localHour = date.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              });
              return { ...stat, hour: localHour };
            }
            return stat;
          });
          setActivityData(localizedData);
        }
      } catch (error) {
        console.error('Failed to load activity data:', error);
      } finally {
        setLoadingActivity(false);
      }
    };

    loadActivityData();
    const interval = setInterval(loadActivityData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Generate sample data if no real data available (using local time)
  const chartData = activityData.length > 0 ? activityData :
    Array.from({ length: 24 }, (_, i) => {
      const now = new Date();
      const hourDate = new Date(now.getTime() - (23 - i) * 60 * 60 * 1000);
      return {
        hour: hourDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }),
        success: Math.floor(Math.random() * 100),
        failure: Math.floor(Math.random() * 10),
        commands: Math.floor(Math.random() * 50)
      };
    });

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        System Activity (Last 24 Hours)
      </Typography>

      {loadingActivity && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="colorFailure" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={theme.palette.error.main} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={theme.palette.error.main} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="hour" tick={{ fontSize: 10 }} stroke={theme.palette.text.secondary} />
            <YAxis tick={{ fontSize: 10 }} stroke={theme.palette.text.secondary} />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="success"
              name="Successful"
              stroke={theme.palette.success.main}
              fillOpacity={1}
              fill="url(#colorSuccess)"
            />
            <Area
              type="monotone"
              dataKey="failure"
              name="Failed"
              stroke={theme.palette.error.main}
              fillOpacity={1}
              fill="url(#colorFailure)"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
};

// Recent Device Activity Panel
const RecentDeviceActivity = ({ loading }) => {
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadActivities = async () => {
      setLoadingActivities(true);
      try {
        const result = await monitoringApi.getRecentDeviceActivity({ limit: 20, hours: 24 });
        if (result.success && result.data.activities) {
          setActivities(result.data.activities);
        }
      } catch (error) {
        console.error('Failed to load recent activities:', error);
      } finally {
        setLoadingActivities(false);
      }
    };

    loadActivities();
    const interval = setInterval(loadActivities, 30000);
    return () => clearInterval(interval);
  }, []);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'mqtt_command':
        return <DeviceIcon fontSize="small" color="primary" />;
      case 'mqtt_response':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'api':
        return <InfoIcon fontSize="small" color="info" />;
      case 'ping':
        return <TrendingUpIcon fontSize="small" />;
      default:
        return <HistoryIcon fontSize="small" />;
    }
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'success': return 'success';
      case 'failure': return 'error';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  return (
    <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Recent Device Activity
      </Typography>

      {loadingActivities && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : activities.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No recent activity
        </Typography>
      ) : (
        <List dense>
          {activities.map((activity, idx) => (
            <ListItem
              key={activity.id || idx}
              button
              onClick={() => activity.deviceId && navigate(`/devices?selectedId=${activity.deviceId}`)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {getActivityIcon(activity.check_type)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {activity.deviceName || activity.device_identifier || 'Unknown Device'}
                    </Typography>
                    <Chip
                      size="small"
                      label={activity.check_type}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {activity.details?.action || activity.details?.eventType || 'Activity'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimeAgo(activity.checked_at)}
                    </Typography>
                  </Box>
                }
              />
              <Chip
                size="small"
                label={activity.result}
                color={getResultColor(activity.result)}
                sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

// Main Dashboard Component
export default function MonitoringDashboard() {
  const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ online: 0, offline: 0, warning: 0, unknown: 0 });
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [alertHistory, setAlertHistory] = useState([]);
  const [services, setServices] = useState({});
  const [uptimeData, setUptimeData] = useState({ overallUptime: '99.9', dailyUptime: [] });
  const [systemHealth, setSystemHealth] = useState({ services: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((message) => {
    console.log('[MonitoringDashboard] WebSocket message:', message);

    switch (message.type) {
      case 'alert_created':
        setAlerts(prev => {
          if (prev.some(a => a.id === message.alert?.id)) return prev;
          return [message.alert, ...prev];
        });
        break;

      case 'alert_resolved':
        setAlerts(prev => prev.filter(a => a.id !== message.alertId));
        break;

      case 'alert_updated':
        setAlerts(prev => prev.map(a =>
          a.id === message.alert?.id ? { ...a, ...message.alert } : a
        ));
        break;

      case 'device_status_changed':
        setDevices(prev =>
          prev.map(d =>
            (d.deviceId === message.deviceId || d.id === message.deviceId)
              ? { ...d, status: message.newStatus }
              : d
          )
        );
        if (message.previousStatus && message.newStatus) {
          setStats(prevStats => {
            const newStats = { ...prevStats };
            if (message.previousStatus === 'ok') newStats.online = Math.max(0, newStats.online - 1);
            else if (message.previousStatus === 'warning') newStats.warning = Math.max(0, newStats.warning - 1);
            else if (['error', 'dead'].includes(message.previousStatus)) newStats.offline = Math.max(0, newStats.offline - 1);
            else newStats.unknown = Math.max(0, newStats.unknown - 1);
            if (message.newStatus === 'ok') newStats.online++;
            else if (message.newStatus === 'warning') newStats.warning++;
            else if (['error', 'dead'].includes(message.newStatus)) newStats.offline++;
            else newStats.unknown++;
            return newStats;
          });
        }
        break;

      case 'device_heartbeat':
        setDevices(prev =>
          prev.map(d =>
            (d.deviceId === message.deviceId || d.id === message.deviceId)
              ? { ...d, lastHeartbeat: new Date().toISOString() }
              : d
          )
        );
        break;

      default:
        break;
    }
  }, []);

  const { connectionStatus, isConnected } = useMonitoringWebSocket({
    autoConnect: true,
    onMessage: handleWebSocketMessage
  });

  const loadDashboardData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const [statusData, deduplicatedAlertsData, uptimeResult, healthResult] = await Promise.all([
        monitoringApi.getFleetStatus(),
        monitoringApi.getDeduplicatedAlerts({ includeResolved: true, days: 7 }),
        monitoringApi.getUptimeStats({ days: 90 }),
        monitoringApi.getMonitoringServiceStatus().catch(() => ({ success: false }))
      ]);

      if (statusData.success) {
        setStats(statusData.data.stats || { online: 0, offline: 0, warning: 0, unknown: 0 });

        const deviceMap = new Map();
        (statusData.data.devices || []).forEach(device => {
          const key = device.deviceId || device.id;
          const existing = deviceMap.get(key);
          if (!existing ||
              (device.lastHeartbeat && (!existing.lastHeartbeat ||
               new Date(device.lastHeartbeat) > new Date(existing.lastHeartbeat)))) {
            deviceMap.set(key, device);
          }
        });
        setDevices(Array.from(deviceMap.values()));

        setServices(statusData.data.services || {});
      }

      if (deduplicatedAlertsData.success && deduplicatedAlertsData.data) {
        setAlerts(deduplicatedAlertsData.data.activeAlerts || []);
        setAlertsSummary(deduplicatedAlertsData.data.summary || null);
        setAlertHistory(deduplicatedAlertsData.data.recentHistory || []);
      }

      if (uptimeResult.success) {
        setUptimeData(uptimeResult.data);
      }

      if (healthResult.success) {
        setSystemHealth(healthResult.data);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      enqueueSnackbar(t('monitoring.loadError') || 'Failed to load monitoring data', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(() => {
      loadDashboardData(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [loadDashboardData]);

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await monitoringApi.acknowledgeAlert(alertId);
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      enqueueSnackbar(t('monitoring.alertAcknowledged') || 'Alert acknowledged', {
        variant: 'success'
      });
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
      enqueueSnackbar(t('monitoring.acknowledgeError') || 'Failed to acknowledge alert', {
        variant: 'error'
      });
    }
  };

  const handleMarkAlertAsFixed = async (alertId) => {
    try {
      const result = await monitoringApi.markAlertAsFixed(alertId);
      if (result.success) {
        setAlerts((prev) => prev.filter((a) => a.id !== alertId));
        enqueueSnackbar('Alert marked as fixed', { variant: 'success' });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Failed to mark alert as fixed:', error);
      enqueueSnackbar('Failed to mark alert as fixed', { variant: 'error' });
    }
  };

  const handleCleanupDuplicates = async () => {
    try {
      const result = await monitoringApi.cleanupDuplicateAlerts();
      if (result.success) {
        enqueueSnackbar(`Cleaned up ${result.data.resolvedCount} duplicate alerts`, {
          variant: 'success'
        });
        loadDashboardData(false);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Failed to cleanup duplicates:', error);
      enqueueSnackbar('Failed to cleanup duplicate alerts', { variant: 'error' });
    }
  };

  const handleDeviceClick = (device) => {
    const deviceId = device.id || device.deviceDbId || device.deviceId;
    if (deviceId) {
      navigate(`/devices?selectedId=${deviceId}`);
    }
  };

  const handleAlertDeviceClick = (deviceId) => {
    navigate(`/devices?selectedId=${deviceId}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">
            {t('monitoring.title') || 'Monitoring Dashboard'}
          </Typography>
          <Chip
            size="small"
            icon={isConnected ? <CheckCircleIcon /> : <ErrorIcon />}
            label={isConnected ? 'Live' : connectionStatus}
            color={isConnected ? 'success' : 'default'}
          />
        </Box>
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={() => loadDashboardData(false)}
          disabled={refreshing}
        >
          {t('Common.refresh') || 'Refresh'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* System Health Overview - Simplified */}
        <Grid item xs={12} md={6}>
          <SystemHealthCard systemHealth={systemHealth} loading={loading} />
        </Grid>

        {/* Fleet Overview */}
        <Grid item xs={12} md={6}>
          <FleetOverview stats={stats} loading={loading} />
        </Grid>

        {/* System Activity Graph */}
        <Grid item xs={12}>
          <SystemActivityGraph loading={loading} />
        </Grid>

        {/* Uptime History - Full Width with click-through */}
        <Grid item xs={12}>
          <UptimeHistory uptimeData={uptimeData} loading={loading} />
        </Grid>

        {/* Alerts Panel with Mark as Fixed */}
        <Grid item xs={12} md={6}>
          <AlertsPanel
            alerts={alerts}
            alertsSummary={alertsSummary}
            alertHistory={alertHistory}
            onAcknowledge={handleAcknowledgeAlert}
            onMarkAsFixed={handleMarkAlertAsFixed}
            onCleanupDuplicates={handleCleanupDuplicates}
            onDeviceClick={handleAlertDeviceClick}
            loading={loading}
          />
        </Grid>

        {/* Service Status - Expandable */}
        <Grid item xs={12} md={6}>
          <ServiceStatusSummary
            services={services}
            onDeviceClick={handleDeviceClick}
            loading={loading}
          />
        </Grid>

        {/* Recent Device Activity */}
        <Grid item xs={12} md={6}>
          <RecentDeviceActivity loading={loading} />
        </Grid>

        {/* Problem Devices Table - Replaces grid */}
        <Grid item xs={12} md={6}>
          <ProblemDevicesTable
            devices={devices}
            onDeviceClick={handleDeviceClick}
            loading={loading}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
